import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { candidatesApi, jobsApi, tagsApi, formatApiError } from '../../lib/api';
import { handleAnalyzeError } from '../../lib/quotaGate';
import { useRealtimeEvent } from '../../contexts/RealtimeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import {
    Upload, Search, Users, FileText, Loader2, Eye, Trash2, X,
    Brain, Filter, ChevronDown, Download, Target, AlertCircle, Trophy, CheckCircle, ChevronLeft, ChevronRight,
    Tag, Plus, AlertTriangle
} from 'lucide-react';
import { pollUploadTask, isTaskGoneError } from '../../lib/uploadTaskStatus';
import { hasScore } from '../../lib/score';
import TagInput from '../../components/TagInput';
import TagBadge from '../../components/TagBadge';
import AISearchBar from '../../components/AISearchBar';
import OcrBadge from '../../components/OcrBadge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CandidatesListPage() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const isTopOnly = searchParams.get('top') === 'true';
    const [candidates, setCandidates] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [jobFilter, setJobFilter] = useState('all');
    const [cvTypeFilter, setCvTypeFilter] = useState('all'); // 'all' | 'standard' | 'ocr'
    const [uploadOpen, setUploadOpen] = useState(false);
    const [selectedJob, setSelectedJob] = useState('');
    const [deleteCandidate, setDeleteCandidate] = useState(null);
    const [analyzing, setAnalyzing] = useState({});
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const PAGE_SIZE = 10;
    const navigate = useNavigate();

    // File queue state
    const [fileQueue, setFileQueue] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    // Tags
    const [selectedTagFilters, setSelectedTagFilters] = useState([]); // multi-select
    const [tagRegistry, setTagRegistry] = useState([]); // [{id,name,category,color,...}]
    const [tagFilterOpen, setTagFilterOpen] = useState(false);
    const tagFilterRef = useRef(null);
    // AI semantic search
    const [aiQuery, setAiQuery] = useState('');
    const [aiResult, setAiResult] = useState(null); // { results: [...], total_considered, total_returned }

    useEffect(() => {
        loadData();
    }, [statusFilter, jobFilter, cvTypeFilter, isTopOnly, page, selectedTagFilters]);

    useEffect(() => {
        setPage(1);
    }, [statusFilter, jobFilter, cvTypeFilter, isTopOnly, selectedTagFilters]);

    useEffect(() => {
        loadTagRegistry();
    }, []);

    // Upload tracking lifecycle: aborts pollers and closes SSE streams on unmount.
    const mountedRef = useRef(true);
    const uploadAbortRef = useRef(new AbortController());
    const activeStreamsRef = useRef(new Set());
    useEffect(() => {
        mountedRef.current = true;
        if (uploadAbortRef.current.signal.aborted) uploadAbortRef.current = new AbortController();
        const streams = activeStreamsRef.current;
        return () => {
            mountedRef.current = false;
            uploadAbortRef.current.abort();
            streams.forEach(close => close());
            streams.clear();
        };
    }, []);
    const updateQueue = (fn) => { if (mountedRef.current) setFileQueue(fn); };

    // Close tag filter dropdown on outside click
    useEffect(() => {
        if (!tagFilterOpen) return undefined;
        const onDoc = (e) => {
            if (tagFilterRef.current && !tagFilterRef.current.contains(e.target)) {
                setTagFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [tagFilterOpen]);

    const loadData = async () => {
        try {
            const params = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
            if (statusFilter !== 'all') params.status = statusFilter;
            if (jobFilter !== 'all') params.job_id = jobFilter;
            if (cvTypeFilter !== 'all') params.cv_type = cvTypeFilter;
            if (search) params.search = search;
            if (isTopOnly) params.top_only = true;
            if (selectedTagFilters.length > 0) params.tags = selectedTagFilters.join(',');

            const [candidatesRes, jobsRes] = await Promise.all([
                candidatesApi.getAll(params),
                jobsApi.getAll(undefined, 0, 200)
            ]);
            setCandidates(candidatesRes.data.candidates || []);
            setTotal(candidatesRes.data.total || 0);
            setTotalPages(candidatesRes.data.pages || 1);
            setJobs(jobsRes.data.jobs || []);
        } catch (error) {
            toast.error(t('errors.generic_load'));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = useCallback(() => {
        setPage(1);
        loadData();
    }, [search, statusFilter, jobFilter]);

    const loadTagRegistry = async () => {
        try {
            const res = await tagsApi.list();
            setTagRegistry(res.data.tags || []);
        } catch { /* ignore */ }
    };

    const persistCandidateTags = async (candidateId, nextTags) => {
        try {
            await candidatesApi.updateTags(candidateId, 'set', nextTags);
            // Refresh registry to pick up newly created custom tags
            loadTagRegistry();
            // Optimistic local update on the row
            setCandidates((prev) => prev.map((c) => (
                c.id === candidateId ? { ...c, tags: nextTags } : c
            )));
        } catch (error) {
            toast.error(formatApiError(error));
            // On error, reload to restore canonical state
            loadData();
        }
    };

    const toggleTagFilter = (tagName) => {
        setSelectedTagFilters((prev) => (
            prev.includes(tagName) ? prev.filter((t) => t !== tagName) : [...prev, tagName]
        ));
    };

    const clearTagFilters = () => setSelectedTagFilters([]);

    // Realtime: score / status arrive live during a batch analysis so
    // the candidate rows update without any manual refresh. We update in
    // place — no full list reload — because a big Enterprise batch can
    // emit 1500 of these events in a few minutes.
    useRealtimeEvent('candidate.updated', (payload) => {
        setCandidates((prev) => prev.map((c) => (
            c.id === payload.id
                ? {
                    ...c,
                    ...('score' in payload ? { score: payload.score } : {}),
                    ...(Array.isArray(payload.tags) ? { tags: payload.tags } : {}),
                    status: payload.status || c.status,
                }
                : c
        )));
    });

    const handleAIResults = (q, payload) => {
        setAiQuery(q);
        setAiResult(payload);
        // Reset traditional filter UI to avoid confusion
        setPage(1);
    };

    const handleAIClear = () => {
        setAiQuery('');
        setAiResult(null);
    };

    // --- File Queue Management ---
    const addFilesToQueue = (files) => {
        const allowed = [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'image/png', 'image/jpeg', 'image/jpg', 'image/webp',
            'image/tiff', 'image/tif', 'image/bmp',
        ];
        const newFiles = Array.from(files)
            .filter(f => allowed.includes(f.type) && f.size <= 10 * 1024 * 1024)
            .map(f => ({
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                file: f,
                name: f.name,
                size: f.size,
                status: 'queued',
                progress: 0,
                step: t('candidates.step_waiting'),
                candidateId: null
            }));
        
        if (newFiles.length < files.length) {
            toast.error(t('candidates.import_some_ignored'));
        }
        if (newFiles.length > 0) {
            setFileQueue(prev => [...prev, ...newFiles]);
        }
    };

    const removeFromQueue = (fileId) => {
        setFileQueue(prev => prev.filter(f => f.id !== fileId));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (!selectedJob || selectedJob === 'none') return;
        addFilesToQueue(e.dataTransfer.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (selectedJob && selectedJob !== 'none') setDragOver(true);
    };

    const handleFileSelect = (e) => {
        if (e.target.files) {
            addFilesToQueue(e.target.files);
            e.target.value = '';
        }
    };

    const processQueue = async () => {
        const pending = fileQueue.filter(f => f.status === 'queued');
        if (pending.length === 0) return;

        setIsProcessing(true);
        const uploadedIds = [];
        const jobId = selectedJob;
        let unconfirmedCount = 0;

        for (const item of pending) {
            if (!mountedRef.current) return;
            const fileId = item.id;
            try {
                updateQueue(prev => prev.map(f => f.id === fileId
                    ? { ...f, status: 'uploading', progress: 5, step: t('candidates.step_uploading') }
                    : f));
                if (await uploadOneFile(item, jobId, uploadedIds) === 'unconfirmed') unconfirmedCount += 1;
            } catch (error) {
                const errorMsg = error.response?.data?.detail?.message
                    || (typeof error.response?.data?.detail === 'string' ? error.response.data.detail : null)
                    || error.message
                    || 'Erreur';
                updateQueue(prev => prev.map(f => f.id === fileId
                    ? { ...f, status: 'error', step: errorMsg, progress: 0 }
                    : f));
                if (error.response?.status === 403) {
                    toast.error(errorMsg);
                    break;
                }
            }
        }

        if (!mountedRef.current) return;
        setIsProcessing(false);
        if (unconfirmedCount > 0) {
            toast.warning(t('candidates.recovery_toast', { count: unconfirmedCount }), { duration: 15000 });
        }

        if (uploadedIds.length > 0) {
            toast.success(`${uploadedIds.length} CV importe(s) avec succes`);
            setTimeout(() => {
                if (!mountedRef.current) return;
                resetUploadState();
                navigate(`/dashboard/candidates/recent?ids=${uploadedIds.join(',')}`);
            }, 1000);
        }
    };

    // Duplicate-CV confirmation dialog. Populated when the backend
    // returns HTTP 409 for a file already imported for this job. The
    // upload loop pauses until the recruiter picks "keep" or "replace".
    const [duplicateDialog, setDuplicateDialog] = useState(null); // { fileId, existing }
    const duplicateResolverRef = useRef(null);

    const askDuplicateResolution = (fileId, existing) => new Promise((resolve) => {
        duplicateResolverRef.current = resolve;
        setDuplicateDialog({ fileId, existing });
    });

    const resolveDuplicate = (choice) => {
        // choice: 'keep' | 'replace'
        const resolve = duplicateResolverRef.current;
        duplicateResolverRef.current = null;
        setDuplicateDialog(null);
        if (resolve) resolve(choice);
    };

    // Upload a single file with SSE progress. Handles the 409 duplicate
    // path by asking the recruiter — one dialog at a time is enough
    // because the outer loop is sequential.
    const uploadOneFile = async (item, jobId, uploadedIds) => {
        const fileId = item.id;
        let force = false;
        for (;;) {
            let response;
            try {
                response = await candidatesApi.uploadSSE(item.file, jobId, force);
            } catch (error) {
                if (error.response?.status === 409 && error.response?.data?.detail?.code === 'duplicate_cv') {
                    const existing = error.response.data.detail.existing_candidate;
                    updateQueue(prev => prev.map(f => f.id === fileId
                        ? { ...f, status: 'duplicate_prompt', step: t('candidates.duplicate_pending'), progress: 0 }
                        : f));
                    const choice = await askDuplicateResolution(fileId, existing);
                    if (choice === 'keep') {
                        updateQueue(prev => prev.map(f => f.id === fileId
                            ? { ...f, status: 'skipped', step: t('candidates.duplicate_skipped'), progress: 100 }
                            : f));
                        if (existing?.id) uploadedIds.push(existing.id);
                        return;
                    }
                    force = true;
                    continue;  // retry with ?force=true
                }
                // 429 concurrency cap reached — friendly user message,
                // mark the file as failed without a stack trace toast.
                if (error.response?.status === 429 && error.response?.data?.detail?.code === 'concurrency_limit_reached') {
                    const msg = error.response.data.detail.message
                        || "Trop d'imports sont en cours simultanément. Merci de patienter quelques secondes avant de renvoyer vos fichiers.";
                    toast.error(msg);
                    updateQueue(prev => prev.map(f => f.id === fileId
                        ? { ...f, status: 'error', step: msg, progress: 0 }
                        : f));
                    return;
                }
                // 403 quota-side blockers: pending cap or extraction exhausted.
                if (error.response?.status === 403 && error.response?.data?.detail?.code) {
                    const detail = error.response.data.detail;
                    toast.error(detail.message || 'Quota atteint');
                    updateQueue(prev => prev.map(f => f.id === fileId
                        ? { ...f, status: 'error', step: detail.message || 'Quota atteint', progress: 0 }
                        : f));
                    return;
                }
                throw error;
            }
            const taskId = response.data?.task_id;
            if (!taskId) throw new Error('No task_id');
            return trackUploadTask(taskId, fileId, uploadedIds);
        }
    };

    // Follows the ORIGINAL task only (read-only): SSE first, then status polling
    // on interruption. Never re-sends the file. Returns 'unconfirmed' when the
    // result could not be confirmed by the backend.
    const trackUploadTask = async (taskId, fileId, uploadedIds) => {
        const url = `${API}/api/candidates/upload-progress/${taskId}`;
        const sse = await streamUploadTask(url, fileId);
        let outcome = sse;
        if (sse.outcome === 'interrupted') {
            updateQueue(prev => prev.map(f => f.id === fileId
                ? { ...f, status: 'recovering', step: t('candidates.recovery_checking') } : f));
            outcome = await pollUploadTask({
                url,
                signal: uploadAbortRef.current.signal,
                onProgress: (data) => updateQueue(prev => prev.map(f => f.id === fileId
                    ? { ...f, progress: data.progress ?? f.progress } : f)),
            });
        }
        if (outcome.outcome === 'aborted') return 'aborted';
        if (outcome.outcome === 'complete') {
            const candidateId = outcome.data.candidate?.id;
            if (candidateId) uploadedIds.push(candidateId);
            updateQueue(prev => prev.map(f => f.id === fileId ? {
                ...f, status: 'complete', progress: 100, step: outcome.data.step || f.step,
                candidateId: candidateId || f.candidateId,
            } : f));
            return 'complete';
        }
        if (outcome.outcome === 'error') throw new Error(outcome.message);
        const msg = outcome.outcome === 'unauthorized' ? t('candidates.recovery_unauthorized') : t('candidates.recovery_unavailable');
        updateQueue(prev => prev.map(f => f.id === fileId ? { ...f, status: 'unconfirmed', step: msg } : f));
        return 'unconfirmed';
    };

    // Normal SSE flow. Resolves (never rejects) with complete / error /
    // interrupted (connection error or 120s without any frame) / aborted (unmount).
    const streamUploadTask = (url, fileId) => new Promise((resolve) => {
        const es = new EventSource(url, { withCredentials: true });
        let timer = null;
        const armStallTimer = () => {
            clearTimeout(timer);
            timer = setTimeout(() => settle({ outcome: 'interrupted' }), 120000);
        };
        const settle = (result) => {
            clearTimeout(timer);
            es.close();
            activeStreamsRef.current.delete(onUnmount);
            resolve(result);
        };
        const onUnmount = () => settle({ outcome: 'aborted' });
        activeStreamsRef.current.add(onUnmount);
        es.onmessage = (event) => {
            armStallTimer();
            let data;
            try { data = JSON.parse(event.data); } catch { return; }
            if (isTaskGoneError(data)) return settle({ outcome: 'interrupted' });
            updateQueue(prev => prev.map(f => f.id === fileId ? {
                ...f,
                status: data.status,
                progress: data.progress,
                step: data.step || f.step,
                candidateId: data.candidate?.id || f.candidateId,
            } : f));
            if (data.status === 'complete') settle({ outcome: 'complete', data });
            else if (data.status === 'error') settle({ outcome: 'error', message: data.error || 'Erreur' });
        };
        es.onerror = () => settle({ outcome: 'interrupted' });
        armStallTimer();
    });

    const resetUploadState = () => {
        setUploadOpen(false);
        setIsProcessing(false);
        setFileQueue([]);
        setSelectedJob('');
        setDragOver(false);
    };

    // --- Export ---
    const handleExport = async (format) => {
        try {
            const params = { format };
            if (statusFilter !== 'all') params.status = statusFilter;
            if (jobFilter !== 'all') params.job_id = jobFilter;
            if (cvTypeFilter !== 'all') params.cv_type = cvTypeFilter;
            if (search) params.search = search;
            if (isTopOnly) params.top_only = true;

            const response = await candidatesApi.exportCandidates(params);
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = format === 'xlsx' ? 'candidats.xlsx' : 'candidats.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success(t('candidates.export_success', { format: format.toUpperCase() }));
        } catch (error) {
            toast.error(formatApiError(error));
        }
    };

    const handleAnalyze = async (candidateId) => {
        setAnalyzing({ ...analyzing, [candidateId]: true });
        try {
            await candidatesApi.analyze(candidateId);
            toast.success(t('candidates.analyze_success'));
            loadData();
        } catch (error) {
            // Quota errors → role-aware dialog (overage / upgrade);
            // anything else falls back to a regular toast.
            handleAnalyzeError(error, () => toast.error(formatApiError(error)));
        } finally {
            setAnalyzing({ ...analyzing, [candidateId]: false });
        }
    };

    const handleDelete = async () => {
        if (!deleteCandidate) return;
        try {
            await candidatesApi.delete(deleteCandidate.id);
            toast.success(t('candidates.trashed_success'));
            setDeleteCandidate(null);
            loadData();
        } catch (error) {
            toast.error(formatApiError(error));
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return { bg: 'bg-[#D1FAE5]', text: 'text-[#059669]' };
        if (score >= 60) return { bg: 'bg-[#DBEAFE]', text: 'text-[#2563EB]' };
        if (score >= 40) return { bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]' };
        return { bg: 'bg-[#FEE2E2]', text: 'text-[#DC2626]' };
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'analyzed':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#D1FAE5] text-[#059669]">{t('candidates.badge_analyzed')}</span>;
            case 'pending':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#FEF3C7] text-[#D97706]">{t('candidates.badge_pending')}</span>;
            case 'analysis_failed':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#FEE2E2] text-[#DC2626]">{t('candidates.badge_failed')}</span>;
            default:
                return null;
        }
    };

    const filteredCandidates = candidates.filter(c => 
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.email?.toLowerCase().includes(search.toLowerCase()) ||
        c.skills?.some(s => s.toLowerCase().includes(search.toLowerCase()))
    );

    const queuedCount = fileQueue.filter(f => f.status === 'queued').length;
    const processingCount = fileQueue.filter(f => !['queued', 'complete', 'error', 'unconfirmed'].includes(f.status)).length;
    const completedCount = fileQueue.filter(f => f.status === 'complete').length;
    const errorCount = fileQueue.filter(f => f.status === 'error').length;
    const unconfirmedCount = fileQueue.filter(f => f.status === 'unconfirmed').length;

    return (
        <div className="space-y-6" data-testid="candidates-list-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] flex items-center gap-2">
                        {isTopOnly && <Trophy className="w-6 h-6 text-[#F59E0B]" />}
                        {isTopOnly ? t('candidates.top_title') : t('candidates.page_title')}
                    </h1>
                    <p className="text-[#4B5563]">
                        {isTopOnly ? t('candidates.top_subtitle') : t('candidates.subtitle')}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    {/* Export dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild disabled={candidates.length === 0}>
                            <Button
                                variant="outline"
                                disabled={candidates.length === 0}
                                title={candidates.length === 0 ? t('candidates.no_candidates_to_export') : undefined}
                                data-testid="export-btn"
                                className="flex-1 sm:flex-initial"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                {t('candidates.export')}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv-btn">
                                <FileText className="w-4 h-4 mr-2" />
                                {t('candidates.export_csv')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="export-xlsx-btn">
                                <FileText className="w-4 h-4 mr-2" />
                                {t('candidates.export_xlsx')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {!isTopOnly && (
                        <Button 
                            className="bg-[#002FA7] hover:bg-[#002480] text-white flex-1 sm:flex-initial"
                            onClick={() => setUploadOpen(true)}
                            data-testid="upload-cv-btn"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            {t('candidates.import_cvs')}
                        </Button>
                    )}
                </div>
            </div>

            {/* AI semantic search */}
            {!isTopOnly && (
                <AISearchBar
                    onResults={handleAIResults}
                    onClear={handleAIClear}
                    active={Boolean(aiResult)}
                />
            )}

            {/* Filters (hidden in AI search mode) */}
            {!aiResult && (
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                    <Input
                        placeholder={t('candidates.search_placeholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        className="pl-10"
                        data-testid="candidate-search-input"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-[150px]">
                        <SelectValue placeholder={t('candidates.filter_status')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('common.all')}</SelectItem>
                        <SelectItem value="pending">{t('candidates.badge_pending')}</SelectItem>
                        <SelectItem value="analyzed">{t('candidates.badge_analyzed')}</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={jobFilter} onValueChange={setJobFilter}>
                    <SelectTrigger className="w-full sm:w-[200px]">
                        <SelectValue placeholder={t('candidates.filter_job')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('candidates.filter_all_jobs')}</SelectItem>
                        {jobs.map((job) => (
                            <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={cvTypeFilter} onValueChange={setCvTypeFilter}>
                    <SelectTrigger
                        className="w-full sm:w-[180px]"
                        data-testid="cv-type-filter-trigger"
                    >
                        <SelectValue placeholder={t('candidates.filter_cv_type')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all" data-testid="cv-type-option-all">
                            {t('candidates.filter_cv_type_all')}
                        </SelectItem>
                        <SelectItem value="standard" data-testid="cv-type-option-standard">
                            {t('candidates.filter_cv_type_standard')}
                        </SelectItem>
                        <SelectItem value="ocr" data-testid="cv-type-option-ocr">
                            {t('candidates.filter_cv_type_ocr')}
                        </SelectItem>
                    </SelectContent>
                </Select>
                {tagRegistry.length > 0 && (
                    <div className="relative" ref={tagFilterRef}>
                        <button
                            type="button"
                            onClick={() => setTagFilterOpen((v) => !v)}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm text-[#0A0A0A] hover:bg-[#F9FAFB] focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                            data-testid="tag-filter-toggle"
                        >
                            <Tag className="w-3.5 h-3.5 text-[#9CA3AF]" />
                            <span>
                                {selectedTagFilters.length === 0
                                    ? t('tags.filter_by_tags')
                                    : t('tags.selected_tags', { count: selectedTagFilters.length })}
                            </span>
                            <ChevronDown className="w-3.5 h-3.5 text-[#9CA3AF]" />
                        </button>
                        {tagFilterOpen && (
                            <div
                                className="absolute z-40 right-0 mt-1 w-72 max-h-80 overflow-auto bg-white border border-[#E5E7EB] rounded-md shadow-lg p-2"
                                data-testid="tag-filter-popover"
                            >
                                <div className="flex items-center justify-between px-1 pb-2 border-b border-[#F3F4F6] mb-1">
                                    <span className="text-xs font-semibold text-[#4B5563] uppercase tracking-wider">
                                        {t('tags.filter_by_tags')}
                                    </span>
                                    {selectedTagFilters.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={clearTagFilters}
                                            className="text-xs text-[#002FA7] hover:underline"
                                            data-testid="tag-filter-clear"
                                        >
                                            {t('tags.clear_filter')}
                                        </button>
                                    )}
                                </div>
                                {/* Group by category for fast scanning */}
                                {['pipeline', 'action', 'priority', 'availability', 'matching', 'custom'].map((cat) => {
                                    const inCat = tagRegistry.filter((tg) => tg.category === cat);
                                    if (inCat.length === 0) return null;
                                    return (
                                        <div key={cat} className="mb-2">
                                            <div className="px-1 py-1 text-[10px] uppercase tracking-wider text-[#9CA3AF]">
                                                {t(`tags.cat_${cat}`)}
                                            </div>
                                            <div className="flex flex-wrap gap-1 px-1">
                                                {inCat.map((tg) => {
                                                    const checked = selectedTagFilters.includes(tg.slug);
                                                    return (
                                                        <button
                                                            key={tg.id}
                                                            type="button"
                                                            onClick={() => toggleTagFilter(tg.slug)}
                                                            className={`transition-all ${checked ? 'ring-2 ring-offset-1 ring-[#002FA7]' : 'opacity-80 hover:opacity-100'}`}
                                                            data-testid={`tag-filter-option-${tg.slug}`}
                                                        >
                                                            <TagBadge slug={tg.slug} registry={tagRegistry} category={tg.category} />
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
            )}

            {/* AI ranked results */}
            {aiResult && (
                <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden" data-testid="ai-results-section">
                    <div className="px-6 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB] flex items-center justify-between">
                        <div className="text-sm text-[#4B5563]">
                            <span className="font-medium text-[#0A0A0A]">«&nbsp;{aiQuery}&nbsp;»</span>
                            {' — '}
                            {t('ai_search.results_summary', {
                                returned: aiResult.total_returned,
                                considered: aiResult.total_considered,
                            })}
                        </div>
                    </div>
                    {aiResult.results.length === 0 ? (
                        <div className="px-6 py-12 text-center">
                            <p className="text-[#4B5563]">{t('ai_search.no_results')}</p>
                            <p className="text-xs text-[#9CA3AF] mt-2">{t('ai_search.considered_help')}</p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#E5E7EB]" data-testid="ai-results-list">
                            {aiResult.results.map((r, i) => {
                                const relColor = r.relevance >= 80 ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                    : r.relevance >= 60 ? 'bg-blue-100 text-blue-700 border-blue-200'
                                    : 'bg-amber-100 text-amber-700 border-amber-200';
                                return (
                                    <li
                                        key={r.id}
                                        onClick={() => navigate(`/dashboard/candidates/${r.id}`)}
                                        className="px-6 py-4 flex items-start gap-4 hover:bg-[#F9FAFB] cursor-pointer transition-colors"
                                        data-testid={`ai-result-${r.id}`}
                                    >
                                        <div className="flex-shrink-0 w-7 text-center text-xs font-bold text-[#9CA3AF] pt-1">
                                            #{i + 1}
                                        </div>
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#002FA7] flex items-center justify-center text-white font-medium">
                                            {r.name?.charAt(0)?.toUpperCase() || '?'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                                <h4 className="text-sm font-semibold text-[#0A0A0A] truncate">
                                                    {r.name || '—'}
                                                </h4>
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold ${relColor}`}>
                                                    {r.relevance}% · {t('ai_search.relevance')}
                                                </span>
                                                {r.matched_job_title && (
                                                    <span className="text-[11px] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded">
                                                        {r.matched_job_title}
                                                    </span>
                                                )}
                                            </div>
                                            {r.reason && (
                                                <p className="text-xs text-[#4B5563] mb-1.5">{r.reason}</p>
                                            )}
                                            <div className="flex flex-wrap items-center gap-1">
                                                {(r.skills || []).slice(0, 6).map((s, si) => (
                                                    <span key={si} className="text-[10px] px-1.5 py-0.5 bg-[#EEF2FF] text-[#4338CA] rounded">
                                                        {s}
                                                    </span>
                                                ))}
                                                {(r.tags || []).slice(0, 3).map((slug) => (
                                                    <TagBadge key={slug} slug={slug} registry={tagRegistry} size="xs" />
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0">
                                            <Button variant="ghost" size="sm" data-testid={`ai-result-view-${r.id}`}>
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}

            {/* Table (hidden in AI search mode) */}
            {!aiResult && (loading ? (
                <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
                </div>
            ) : filteredCandidates.length > 0 ? (
                <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px]">
                            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider">{t('candidates.col_candidate')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider hidden md:table-cell">{t('candidates.col_skills')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider hidden lg:table-cell">{t('candidates.col_job')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider hidden md:table-cell">{t('candidates.col_tags')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider">{t('candidates.col_score')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider hidden sm:table-cell">{t('candidates.col_status')}</th>
                                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#4B5563] uppercase tracking-wider">{t('common.actions')}</th>
                                </tr>
                            </thead>
                        <tbody className="divide-y divide-[#E5E7EB]">
                            {filteredCandidates.map((candidate) => {
                                const scoreColors = hasScore(candidate.score?.overall) ? getScoreColor(candidate.score.overall) : null;
                                return (
                                    <tr key={candidate.id} className="hover:bg-[#F9FAFB] transition-colors" data-testid={`candidate-row-${candidate.id}`}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-[#002FA7] flex items-center justify-center text-white font-medium">
                                                    {candidate.name?.charAt(0)?.toUpperCase() || '?'}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <Link to={`/dashboard/candidates/${candidate.id}`} className="font-medium text-[#0A0A0A] hover:text-[#002FA7]">
                                                            {candidate.name}
                                                        </Link>
                                                        <OcrBadge active={candidate.is_ocr} size="sm" withLabel={false} />
                                                    </div>
                                                    <p className="text-sm text-[#9CA3AF]">{candidate.email || candidate.cv_filename}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 hidden md:table-cell">
                                            <div className="flex flex-wrap gap-1">
                                                {candidate.skills?.slice(0, 3).map((skill, i) => (
                                                    <span key={i} className="px-2 py-0.5 bg-[#F3F4F6] text-[#4B5563] text-xs rounded">{skill}</span>
                                                ))}
                                                {candidate.skills?.length > 3 && (
                                                    <span className="text-xs text-[#9CA3AF]">+{candidate.skills.length - 3}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 hidden lg:table-cell">
                                            {candidate.matched_job_title ? (
                                                <span className="px-2 py-1 bg-[#DBEAFE] text-[#002FA7] text-xs rounded-full">{candidate.matched_job_title}</span>
                                            ) : (
                                                <span
                                                    className="px-2 py-1 bg-[#F3F4F6] text-[#9CA3AF] text-xs rounded-full italic"
                                                    data-testid={`candidate-unassigned-${candidate.id}`}
                                                >
                                                    {t('candidates.unassigned')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 hidden md:table-cell" onClick={(e) => e.stopPropagation()}>
                                            <TagInput
                                                value={candidate.tags || []}
                                                registry={tagRegistry}
                                                onChange={(next) => persistCandidateTags(candidate.id, next)}
                                                compact
                                                data-testid={`candidate-tags-${candidate.id}`}
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            {hasScore(candidate.score?.overall) ? (
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${scoreColors.bg} ${scoreColors.text}`} data-testid={`candidate-score-${candidate.id}`}>
                                                    {candidate.score.overall}
                                                </span>
                                            ) : <span className="text-[#9CA3AF]">-</span>}
                                        </td>
                                        <td className="px-6 py-4 hidden sm:table-cell">{getStatusBadge(candidate.status)}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                {candidate.status === 'pending' && (
                                                    <Button size="sm" variant="outline" onClick={() => handleAnalyze(candidate.id)} disabled={analyzing[candidate.id]} data-testid={`analyze-btn-${candidate.id}`}>
                                                        {analyzing[candidate.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                                                    </Button>
                                                )}
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button size="sm" variant="ghost"><ChevronDown className="w-4 h-4" /></Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => navigate(`/dashboard/candidates/${candidate.id}`)}>
                                                            <Eye className="w-4 h-4 mr-2" />{t('jobs.view_details')}
                                                        </DropdownMenuItem>
                                                        {candidate.can_delete && (
                                                            <DropdownMenuItem onClick={() => setDeleteCandidate(candidate)} className="text-[#EF4444] focus:text-[#EF4444]" data-testid={`delete-btn-${candidate.id}`}>
                                                                <Trash2 className="w-4 h-4 mr-2" />{t('common.delete')}
                                                            </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
                    <Users className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" />
                    <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">{t('candidates.empty_title')}</h3>
                    <p className="text-[#4B5563] mt-2">{t('candidates.empty_message')}</p>
                    <Button className="mt-4 bg-[#002FA7] hover:bg-[#002480] text-white" onClick={() => setUploadOpen(true)}>
                        <Upload className="w-4 h-4 mr-2" />{t('candidates.import_cvs')}
                    </Button>
                </div>
            ))}

            {/* Pagination (hidden in AI search mode) */}
            {!aiResult && totalPages > 1 && (
                <div className="flex items-center justify-between px-2 py-3" data-testid="candidates-pagination">
                    <p className="text-sm text-[#9CA3AF]">{total <= 1 ? t('candidates.total_one', { count: total }) : t('candidates.total_other', { count: total })}</p>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1} data-testid="candidates-prev-page">
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-[#4B5563] min-w-[80px] text-center">{page} / {totalPages}</span>
                        <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} data-testid="candidates-next-page">
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Upload dialog with Drag & Drop + File Queue */}
            <Dialog open={uploadOpen} onOpenChange={(open) => !isProcessing && (open ? setUploadOpen(true) : resetUploadState())}>
                <DialogContent className="sm:max-w-xl">
                    <DialogHeader>
                        <DialogTitle>{t('candidates.import_dialog_title')}</DialogTitle>
                        <DialogDescription>
                            {t('candidates.import_dialog_description')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Job selector */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-[#0A0A0A]">
                                {t('candidates.import_associate_job')} <span className="text-[#EF4444]">*</span>
                            </label>
                            <Select value={selectedJob} onValueChange={setSelectedJob} disabled={isProcessing}>
                                <SelectTrigger data-testid="upload-job-select">
                                    <SelectValue placeholder={t('candidates.import_select_job')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {jobs.filter(j => j.status === 'active').map((job) => (
                                        <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {jobs.filter(j => j.status === 'active').length === 0 && (
                                <p className="text-xs text-[#EF4444]">{t('candidates.import_no_active_jobs')}</p>
                            )}
                        </div>

                        {/* Drop zone */}
                        {(!selectedJob || selectedJob === 'none') ? (
                            <div className="border-2 border-dashed border-[#E5E7EB] rounded-lg p-6 text-center opacity-60">
                                <FileText className="w-10 h-10 text-[#9CA3AF] mx-auto mb-2" />
                                <p className="text-[#EF4444] font-medium text-sm" data-testid="upload-job-required-msg">
                                    {t('candidates.import_select_first')}
                                </p>
                            </div>
                        ) : (
                            <div
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={() => setDragOver(false)}
                                onClick={() => !isProcessing && fileInputRef.current?.click()}
                                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                                    dragOver ? 'border-[#002FA7] bg-[#EFF6FF]' : 'border-[#E5E7EB] hover:border-[#002FA7]'
                                } ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}
                                data-testid="upload-drop-zone"
                            >
                                <Upload className={`w-10 h-10 mx-auto mb-2 ${dragOver ? 'text-[#002FA7]' : 'text-[#9CA3AF]'}`} />
                                <p className="text-[#0A0A0A] font-medium text-sm">
                                    {dragOver ? t('candidates.import_drop_here') : t('candidates.import_drag_or_click')}
                                </p>
                                <p className="text-xs text-[#9CA3AF] mt-1">{t('candidates.import_file_hint')}</p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.docx,.png,.jpg,.jpeg,.webp,.tiff,.bmp"
                                    multiple
                                    onChange={handleFileSelect}
                                    data-testid="file-upload-input"
                                />
                            </div>
                        )}

                        {/* File queue */}
                        {fileQueue.length > 0 && (
                            <div className="space-y-2" data-testid="file-queue">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-[#0A0A0A]">
                                        {fileQueue.length <= 1 ? t('candidates.queue_files_one', { count: fileQueue.length }) : t('candidates.queue_files_other', { count: fileQueue.length })}
                                    </p>
                                    <div className="flex gap-2 text-xs">
                                        {queuedCount > 0 && <span className="text-[#9CA3AF]">{t('candidates.queue_waiting', { count: queuedCount })}</span>}
                                        {processingCount > 0 && <span className="text-[#002FA7]">{t('candidates.queue_processing', { count: processingCount })}</span>}
                                        {completedCount > 0 && <span className="text-[#059669]">{t('candidates.queue_completed', { count: completedCount })}</span>}
                                        {errorCount > 0 && <span className="text-[#EF4444]">{t('candidates.queue_errors', { count: errorCount })}</span>}
                                        {unconfirmedCount > 0 && <span className="text-[#B45309]" data-testid="queue-unconfirmed-count">{t('candidates.queue_unconfirmed', { count: unconfirmedCount })}</span>}
                                    </div>
                                </div>

                                <div className="max-h-48 overflow-y-auto space-y-1.5">
                                    {fileQueue.map((item) => (
                                        <div 
                                            key={item.id} 
                                            className={`flex items-center gap-3 p-2.5 rounded-lg border text-sm ${
                                                item.status === 'complete' ? 'bg-[#F0FDF4] border-[#BBF7D0]' :
                                                item.status === 'error' ? 'bg-[#FEF2F2] border-[#FECACA]' :
                                                item.status === 'queued' ? 'bg-[#F9FAFB] border-[#E5E7EB]' :
                                                item.status === 'unconfirmed' ? 'bg-[#FFFBEB] border-[#FDE68A]' :
                                                'bg-[#EFF6FF] border-[#BFDBFE]'
                                            }`}
                                            data-testid={`queue-item-${item.id}`}
                                            data-status={item.status}
                                        >
                                            {/* Icon */}
                                            {item.status === 'complete' ? (
                                                <CheckCircle className="w-4 h-4 text-[#059669] flex-shrink-0" />
                                            ) : item.status === 'error' ? (
                                                <AlertCircle className="w-4 h-4 text-[#EF4444] flex-shrink-0" />
                                            ) : item.status === 'unconfirmed' ? (
                                                <AlertTriangle className="w-4 h-4 text-[#B45309] flex-shrink-0" />
                                            ) : item.status === 'queued' ? (
                                                <FileText className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />
                                            ) : (
                                                <Loader2 className="w-4 h-4 text-[#002FA7] animate-spin flex-shrink-0" />
                                            )}

                                            {/* File info */}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[#0A0A0A] truncate">{item.name}</p>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className="text-xs text-[#9CA3AF] whitespace-nowrap flex-shrink-0">
                                                        {(item.size / 1024).toFixed(0)} KB
                                                    </span>
                                                    {!['queued', 'complete', 'error', 'unconfirmed'].includes(item.status) && (
                                                        <>
                                                            <span className="text-xs text-[#002FA7]" data-testid={`queue-item-step-${item.id}`}>{item.step}</span>
                                                            <span className="text-xs text-[#002FA7] font-medium">{item.progress}%</span>
                                                        </>
                                                    )}
                                                    {item.status === 'error' && (
                                                        <span className="text-xs text-[#EF4444]" data-testid={`queue-item-error-${item.id}`}>{item.step}</span>
                                                    )}
                                                    {item.status === 'unconfirmed' && (
                                                        <span className="text-xs text-[#B45309]" data-testid={`queue-item-unconfirmed-${item.id}`}>{item.step}</span>
                                                    )}
                                                </div>
                                                {!['queued', 'complete', 'error', 'unconfirmed'].includes(item.status) && (
                                                    <div className="w-full h-1 bg-[#E5E7EB] rounded-full mt-1 overflow-hidden">
                                                        <div className="h-full bg-[#002FA7] rounded-full transition-all" style={{ width: `${item.progress}%` }} />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Remove button */}
                                            {item.status === 'queued' && (
                                                <button onClick={(e) => { e.stopPropagation(); removeFromQueue(item.id); }} className="p-1 hover:bg-[#E5E7EB] rounded">
                                                    <X className="w-3.5 h-3.5 text-[#9CA3AF]" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="flex gap-2">
                        <Button variant="outline" onClick={resetUploadState} disabled={isProcessing}>
                            {isProcessing ? t('common.processing') : t('common.close')}
                        </Button>
                        {fileQueue.some(f => f.status === 'queued') && (
                            <Button 
                                onClick={processQueue}
                                disabled={isProcessing || !selectedJob}
                                className="bg-[#002FA7] hover:bg-[#002480] text-white"
                                data-testid="start-upload-btn"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                {queuedCount <= 1 ? t('candidates.queue_import_btn', { count: queuedCount }) : t('candidates.queue_import_btn_plural', { count: queuedCount })}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <AlertDialog open={!!deleteCandidate} onOpenChange={() => setDeleteCandidate(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('candidates.delete_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('candidates.delete_description', { name: deleteCandidate?.name })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-[#EF4444] hover:bg-[#DC2626] text-white">
                            {t('candidates.delete_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Duplicate CV confirmation — surfaced when the backend
                rejects an upload with HTTP 409 { code: 'duplicate_cv' }. */}
            <AlertDialog
                open={!!duplicateDialog}
                onOpenChange={(o) => { if (!o) resolveDuplicate('keep'); }}
            >
                <AlertDialogContent data-testid="duplicate-cv-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('candidates.duplicate_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('candidates.duplicate_description', {
                                name: duplicateDialog?.existing?.name
                                    || duplicateDialog?.existing?.email
                                    || duplicateDialog?.existing?.cv_filename
                                    || t('candidates.duplicate_unknown_name'),
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            onClick={() => resolveDuplicate('keep')}
                            data-testid="duplicate-cv-keep"
                        >
                            {t('candidates.duplicate_keep_existing')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => resolveDuplicate('replace')}
                            data-testid="duplicate-cv-replace"
                            className="bg-[#002FA7] hover:bg-[#002480]"
                        >
                            {t('candidates.duplicate_replace')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
